import { defineStore } from "pinia";
import { LOGGER } from "@/util/logger";
import { INSPECTION_ENDPOINT, SUBMIT_PDF_REPORT_ENDPOINT } from "@/constants/Endpoints";
import axios from "axios";
import { INSPECTION_STATUSES } from "@/constants/InspectionStatuses";
import { useStructureSearchStore } from "@/stores/structureSearch";
import { HTTP_STATUS_CODE } from "@/constants/CommonWebCodes";
import { RECORD_MANAGEMENT_TYPES } from "@/constants/StructureSearches";
import {
  INSPECTION_PAGES,
  BRIDGE_PAGES,
  WALL_PAGES,
  SIGN_PAGES,
  TUNNEL_PAGES,
  EDOCS_PAGES,
  STATUSES_CAN_SUBMIT,
} from "@/constants/Inspections";
import { clone } from "@/util/clone";
import { CUSTOM_ICONS } from "@/constants/Unicode";
import { ENV_CONFIG_PROPERTY } from "@/constants/EnvConfigProperties";
import { useConfigStore } from "@/stores/config";
import { useUserPermissionStore } from "@/stores/userPermission";
import { ACCESS_LEVELS, INSPECTION_PERMISSIONS } from "@/rbac";

export const useInspectionStore = defineStore("inspection", {
  state: () => ({
    inspections: {},
    selectedInspection: {},
    isDirty: false,
    page: INSPECTION_PAGES.GENERAL,
    nextPage: "",
    nextRoute: "",
    unsavedChangesWarning: false,
    abortController: null,
    addingOrEditing: false,
    latestInspKey: "",
    userAuthenticationErrors: {},
    featureIntersectionTableData: [],
    inspectionArray: [],
    submissionErrors: new Map(),
    overallScourHoleChangedByUser: false,
    pageReference: null,
    showCalculate: false,
    savingSnackbar: false,
    saved: false,
    showSubmitLaterMessage: false,
    reportToSubmit: null,
    seismicVulnerabilityChangedByUser: false,
    nstmInspectionConditionChangedByUser: false,
    showValidationSnackbar: false,
  }),
  getters: {
    getSelectedInspection() {
      return this.selectedInspection;
    },
    isEditable() {
      if (this.latestInspKey == this.selectedInspection?.inspection?.inspectionId) {
        const userPermissionStore = useUserPermissionStore();
        const isUserHasPermissionToEdit = userPermissionStore.checkUserPermission(
          INSPECTION_PERMISSIONS[ACCESS_LEVELS.EDIT],
          this.selectedInspection.permission,
        );
        return (
          isUserHasPermissionToEdit &&
          Boolean(STATUSES_CAN_SUBMIT.includes(this.selectedInspection?.inspection?.inspectionStatus))
        );
      } else {
        return false;
      }
    },
    getDirtyFlag() {
      return this.isDirty;
    },
    getShowValidationSnackbar() {
      return this.showValidationSnackbar;
    },
    getNstmInspectionConditionChangedByUser() {
      return this.nstmInspectionConditionChangedByUser;
    },
    getSeismicVulnerabilityChangedByUser() {
      return this.seismicVulnerabilityChangedByUser;
    },
    getShowCalculate() {
      return this.showCalculate;
    },
    getOverallScourHoleChangedByUser() {
      return this.overallScourHoleChangedByUser;
    },
    getReportToSubmit() {
      return this.reportToSubmit;
    },
    getSavingSnackbar() {
      return this.savingSnackbar;
    },
    getSaved() {
      return this.saved;
    },
    getActiveNavigation() {
      const configStore = useConfigStore();

      const activeInspPages = configStore.getEnvConfigValue(ENV_CONFIG_PROPERTY.ACTIVE_INSPECTION_PAGES)?.split(",");

      return this.getNavigation?.filter((p) => activeInspPages?.includes(p));
    },
    getNavigation() {
      let navigationReturn = null;
      switch (this.selectedInspection?.structureBridge?.servTypOn) {
        case "0":
        case "1":
        case "2":
        case "3":
        case "4":
        case "5":
        case "6":
        case "7":
        case "8":
        case "9":
        case "X":
          navigationReturn = BRIDGE_PAGES;
          break;
        case "H":
        case "S":
          navigationReturn = SIGN_PAGES;
          break;
        case "N":
        case "R":
          navigationReturn = WALL_PAGES;
          break;
        case "T":
          navigationReturn = TUNNEL_PAGES;
          break;
      }
      if (this.selectedInspection?.recordType === RECORD_MANAGEMENT_TYPES.DOCUMENTS) {
        navigationReturn = EDOCS_PAGES;
      }
      return navigationReturn;
    },
    getUserAuthenticationErrors() {
      return this.userAuthenticationErrors;
    },
    getSelectedPage() {
      return this.page;
    },
    getPageReference() {
      return this.pageReference;
    },
    getNextPage() {
      return this.nextPage;
    },
    getNextRoute() {
      return this.nextRoute;
    },
    getUnsavedChangesWarning() {
      return this.unsavedChangesWarning;
    },
    getInspectionArray() {
      return this.inspectionArray;
    },
    getAddingOrEditing() {
      return this.addingOrEditing;
    },
    getSubUnit: (state) => (unitKey) => {
      if (unitKey) {
        return JSON.stringify(unitKey) + " - " + state.getSubStructure(unitKey)?.strUnitLabel;
      } else {
        return "";
      }
    },
    getSubUnits: () => (structureUnits) => {
      let units = structureUnits?.filter((unit) => {
        if (unit.legacyStructureType == "P" || unit.legacyStructureType == "B" || unit.legacyStructureType == "C") {
          return true;
        }
      });
      let returnObject = [];
      for (let unit of units) {
        returnObject.push({
          value: unit.strUnitKey,
          title: `${unit.strUnitKey} - ${unit.strUnitLabel}`,
        });
      }
      return returnObject;
    },
    getAllSubUnits: (state) => () => {
      let returnValue = [];
      for (const unit of state.selectedInspection?.structureUnits ?? []) {
        returnValue.push({
          value: unit.strUnitKey,
          title: `${unit.strUnitKey} - ${unit.strUnitLabel}`,
        });
      }
      return returnValue;
    },
    getStructureUnitLabel: (state) => (unitKey) => {
      return state.selectedInspection?.structureUnits?.find((unit) => unit.strUnitKey == unitKey)?.strUnitLabel;
    },
    getSubStructure: (state) => (unitKey, structureUnits) => {
      if (structureUnits) {
        return structureUnits?.find((unit) => unit.strUnitKey == unitKey);
      } else {
        return state.selectedInspection?.structureUnits?.find((unit) => unit.strUnitKey == unitKey);
      }
    },
    getIDsNotInUnderwaterInsp: () => (inspectionValue) => {
      let idsNotInUnderwater = [];

      for (let idx in inspectionValue.structureUnits) {
        if (
          !inspectionValue?.inspectionsUnderwater?.find(
            (unit) => inspectionValue.structureUnits[idx].strUnitKey == unit.strUnitKey,
          ) &&
          (inspectionValue.structureUnits[idx].legacyStructureType == "B" ||
            inspectionValue.structureUnits[idx].legacyStructureType == "P" ||
            inspectionValue.structureUnits[idx].legacyStructureType == "C" ||
            inspectionValue.structureUnits[idx].legacyStructureType == "W")
        ) {
          idsNotInUnderwater.push({
            value: inspectionValue.structureUnits[idx].strUnitKey,
            title: inspectionValue.structureUnits[idx].strUnitLabel,
          });
        }
      }

      return idsNotInUnderwater;
    },

    getFilteredStructureUnits: (state) => (unitTypes, toInclude) => {
      if (toInclude) {
        return state.selectedInspection?.structureUnits?.filter((unit) => unitTypes.includes(unit.legacyStructureType));
      } else {
        //exclude the unit types
        return state.selectedInspection?.structureUnits?.filter(
          (unit) => !unitTypes.includes(unit.legacyStructureType),
        );
      }
    },
    getFeatureIntersectionTableData: (state) => () => {
      state.featureIntersectionTableData = [];

      for (let i = 0; i < state.selectedInspection?.structureRoadways?.length; i++) {
        state.featureIntersectionTableData.push({
          rowId: i + 1,
          ...state.selectedInspection?.structureRoadways[i],
          ...state.selectedInspection?.userRWays.find(
            (itmInner) => itmInner.featureKey === state.selectedInspection?.structureRoadways[i].featureKey,
          ),
        });
      }
      return state.featureIntersectionTableData;
    },
    getFeatureIntersectionSelectedRow: (state) => (rowId) => {
      return state.featureIntersectionTableData?.find((item) => item.rowId == rowId);
    },
    getSubmissionErrors: (state) => () => {
      return state.submissionErrors;
    },
    getShowSubmitLaterMessage: (state) => () => {
      return state.showSubmitLaterMessage;
    },
    getBrKey: (state) => {
      return state.selectedInspection?.structureBridge?.brkey;
    },
  },
  actions: {
    setDuration(duration, durationChanges) {
      if (durationChanges == 1) {
        duration = 0;
      }
      return duration;
    },
    setDurationChanges(durationChanges = 0) {
      if (durationChanges > 0) {
        durationChanges = durationChanges + 1;
      } else {
        durationChanges = 1;
      }
      return durationChanges;
    },
    getRatingMin(ratings) {
      const validRating = (rating) => rating !== "_" && !!rating;
      const isN = (rating) => rating === "N";
      return ratings.sort((a, b) => {
        if (validRating(a) && !validRating(b)) {
          return 1;
        } else if (!validRating(a) && validRating(b)) {
          return -1;
        } else if (!validRating(a) && !validRating(b)) {
          return 0;
        } else if (isN(a) && !isN(b)) {
          return 1;
        } else if (!isN(a) && isN(b)) {
          return -1;
        } else if (isN(a) && isN(b)) {
          return 0;
        } else {
          return Number.parseInt(a) - Number.parseInt(b);
        }
      });
    },
    setLowestConditionRating() {
      if (
        this.selectedInspection?.structureBridge?.servTypOn >= 0 &&
        this.selectedInspection?.structureBridge?.servTypOn <= 9
      ) {
        const lowestCondition = this.getRatingMin([
          this.selectedInspection.inspection.dkRating,
          this.selectedInspection.inspection.subRating,
          this.selectedInspection.inspection.culvRating,
          this.selectedInspection.inspection.supRating,
        ])[0];
        this.selectedInspection.userInspection.lowestCondition = lowestCondition;
        switch (lowestCondition) {
          case "0":
          case "1":
          case "2":
          case "3":
          case "4":
            this.selectedInspection.inspection.condition = "P";
            break;
          case "5":
          case "6":
            this.selectedInspection.inspection.condition = "F";
            break;
          case "7":
          case "8":
          case "9":
            this.selectedInspection.inspection.condition = "G";
            break;
          default:
            this.selectedInspection.inspection.condition = "N";
            break;
        }
      } else {
        this.selectedInspection.userInspection.lowestCondition = "N";
        this.selectedInspection.inspection.condition = "N";
      }
    },
    async setSelectedInspection(inspection) {
      this.selectedInspection = inspection;
      const userPermissionStore = useUserPermissionStore();
      userPermissionStore.setUserPermissionForOffliceAccess(inspection?.structureBridge?.brkey, inspection?.permission);
    },
    setLatestInspKey(inspkey) {
      this.latestInspKey = inspkey;
    },
    setInspectionArray(inspectionArray) {
      this.inspectionArray = inspectionArray;
    },
    setAddingOrEditing(value) {
      this.addingOrEditing = value;
    },
    setPageReference(value) {
      this.pageReference = value;
    },
    setNstmInspectionConditionChangedByUser(value) {
      this.nstmInspectionConditionChangedByUser = value;
    },
    setSeismicVulnerabilityChangedByUser(value) {
      this.seismicVulnerabilityChangedByUser = value;
    },
    async loadInspection(brkey, inspkey) {
      const result = await this.getInspection(brkey, inspkey);
      this.setSelectedInspection(result);
      await this.updateKeyFields(brkey);
      this.isDirty = false;
    },
    async getInspection(brkey, inspkey) {
      let url = INSPECTION_ENDPOINT.LOAD_INSPECTION;
      url = url.replace("{brkey}", brkey).replace("{inspkey}", inspkey);
      const insp = await axios.get(url).catch((e) => {
        let error = Object.assign(e, {
          method: "getInspection",
          brkey,
          inspkey,
        });
        LOGGER.logException(error);
      });
      return insp?.data?.data;
    },
    async getInspectionKeyFields(brkey, inspkey) {
      if (brkey && inspkey) {
        let url = INSPECTION_ENDPOINT.GET_INSP_KEYFIELDS;
        url = url.replace("{brkey}", brkey).replace("{inspkey}", inspkey);
        const insp = await axios.get(url).catch((e) => {
          let error = Object.assign(e, {
            method: "getInspectionKeyFields",
            brkey,
            inspkey,
          });
          LOGGER.logException(error);
        });
        return insp?.data?.data;
      }
      return {};
    },
    async updateKeyFields(brkey) {
      if (this.inspections[brkey]) {
        const selectedIndex = this.inspections[brkey]?.findIndex(
          (a) => a.inspectionId == this.selectedInspection?.inspection?.inspectionId,
        );
        let inspections = clone(this.inspections[brkey]);
        let prevAcceptedInspkey = inspections
          .splice(selectedIndex + 1)
          .find((a) => a.inspectionStatus == INSPECTION_STATUSES.ACCEPTED)?.inspectionId;
        this.selectedInspection.keyFields = await this.getInspectionKeyFields(brkey, prevAcceptedInspkey);
      }
    },
    async loadLatestInspection(brkey, abortController) {
      const structureSearchStore = useStructureSearchStore();

      let url = INSPECTION_ENDPOINT.LOAD_LATEST_INSPECTION;
      url = url.replace("{brkey}", brkey);
      await axios
        .get(url, {
          signal: abortController.signal,
        })
        .then(async (result) => {
          this.selectedInspection = result.data.data;
          structureSearchStore.isUserAuthenticated = true;
        })
        .then(async () => {
          await this.getInspections(brkey);
          await this.updateKeyFields(brkey);
        })
        .catch((e) => {
          if (e?.response?.status && e.response.status != 200) {
            if (structureSearchStore.userAuthenticationErrors[e.response.status]) {
              structureSearchStore.userAuthenticationErrors[e.response.status].push(brkey);
            } else {
              structureSearchStore.userAuthenticationErrors[e.response.status] = [brkey];
            }
          }

          let error = Object.assign(e, {
            method: "loadLatestInspection",
            brkey,
          });
          LOGGER.logException(error);
        });
    },
    getReportCheckbox(value) {
      if (value === "1") {
        return CUSTOM_ICONS.SQUARE_CHECK;
      } else {
        return CUSTOM_ICONS.SQUARE;
      }
    },
    getOverallScourHole(scourCondRating, scourHole, structure) {
      // If user manually set rating or it exists, return it
      if (scourCondRating || this.overallScourHoleChangedByUser) {
        return scourCondRating;
      }

      // If scourHole exists, use it
      if (scourHole) {
        return scourHole;
      }

      // No underwater inspections available
      if (!structure?.inspectionsUnderwater?.length) {
        return "";
      }

      // Find minimum scour hole from underwater inspections
      let minScourHole = structure.inspectionsUnderwater[0]?.scourHole;
      for (const inspection of structure.inspectionsUnderwater) {
        const currentScourHole = inspection?.scourHole;
        if (Number.parseInt(currentScourHole) < Number.parseInt(minScourHole)) {
          minScourHole = currentScourHole;
        }
      }

      return minScourHole;
    },

    getCommentIndex(structureUnit, commentType) {
      return this.selectedInspection?.structureUnitInspComments.findIndex(
        (element) => element?.strUnitKey == structureUnit?.strUnitKey && element?.commentType == commentType,
      );
    },
    async submitInspection(inspection, isValidate) {
      LOGGER.logEvent(LOGGER.EventAction.Submit, LOGGER.EventCat.Button, "Inspection", inspection);
      this.submissionErrors.set(inspection.structureBridge.brkey, null);
      let endpointURL = isValidate ? INSPECTION_ENDPOINT.VALIDATE_INSPECTION : INSPECTION_ENDPOINT.SUBMIT_INSPECTION;
      await axios
        .post(endpointURL, inspection, {
          headers: {
            // These are not explicitly required by the backend service,
            // but adding them makes it significantly easier to determine
            // which structure a user is working on when looking at the logs
            // in Azure Application Insights
            brkey: inspection.structureBridge.brkey,
            inspectionId: inspection.inspection.inspectionId,
          },
        })
        .then((response) => {
          if (response?.data?.data?.validationMessages?.length > 0) {
            this.setSubmissionErrors(inspection.structureBridge.brkey, response?.data?.data?.validationMessages);
          }
          return response;
        })
        .catch((e) => {
          let error = Object.assign(e, {
            method: isValidate ? "submitInspection" : "validateInspection",
            inspection,
          });
          LOGGER.logException(error);
          if (e?.response?.status && e.response.status == HTTP_STATUS_CODE.NOT_AUTHORIZED) {
            this.userAuthenticationErrors[e.response.status] = [inspection.structureBridge.brkey];
          } else if (e?.response?.status && e.response.status != HTTP_STATUS_CODE.SUCCESS) {
            if (this.submissionErrors.get(inspection.structureBridge.brkey)) {
              this.submissionErrors.get(inspection.structureBridge.brkey).push(e.response.status);
            } else {
              this.submissionErrors.set(inspection.structureBridge.brkey, [e.response.status]);
            }
          } else {
            throw e;
          }
        });
    },
    async submitReport(inspection, file, updateInspectionReportGenerator, isInspectionReportGenerator) {
      LOGGER.logEvent(LOGGER.EventAction.SubmitReport, LOGGER.EventCat.Button, "Report", file.name);
      const formData = new FormData();
      let url = SUBMIT_PDF_REPORT_ENDPOINT;
      url = url.replace("{brkey}", inspection.structureBridge.brkey);
      const jsonData = {
        filename: file?.name,
        description: file?.name,
        inspectionId: inspection?.inspection?.inspectionId,
      };
      if (isInspectionReportGenerator) {
        jsonData.docContainerType = "I";
      }
      formData.append("json", JSON.stringify(jsonData));
      formData.append("file", file);
      await axios
        .post(url, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((response) => {
          this.setSubmissionErrors(inspection.structureBridge.brkey, [response]);
          updateInspectionReportGenerator(response);
        })
        .catch((e) => {
          let error = Object.assign(e, {
            method: "submitReport",
            file,
          });
          LOGGER.logException(error);
          if (e?.response?.status && e.response.status == HTTP_STATUS_CODE.NOT_AUTHORIZED) {
            this.userAuthenticationErrors[e.response.status] = [inspection.structureBridge.brkey];
          } else if (e?.response?.status && e.response.status != HTTP_STATUS_CODE.SUCCESS) {
            this.submissionErrors.set(inspection.structureBridge.brkey, [e.response.status]);
          }
        });
    },
    setSubmissionErrors(brkey, validationMsgs) {
      if (this.submissionErrors.get(brkey)) {
        this.submissionErrors.get(brkey).concat(validationMsgs);
      } else {
        this.submissionErrors.set(brkey, validationMsgs);
      }
    },
    setDirtyFlag(value) {
      this.isDirty = value;
    },
    setShowValidationSnackbar(value) {
      this.showValidationSnackbar = value;
    },
    setShowCalculate(value) {
      this.showCalculate = value;
    },
    setSavingSnackbar(value) {
      this.savingSnackbar = value;
    },
    setSaved(value) {
      this.saved = value;
    },
    setSelectedPage(value) {
      this.page = value;
    },
    setNextPage(value) {
      this.nextPage = value;
    },
    setNextRoute(value) {
      this.nextRoute = value;
    },
    setOverallScourHoleChangedByUser(value) {
      this.overallScourHoleChangedByUser = value;
    },
    setUnsavedChangesWarning(value) {
      this.unsavedChangesWarning = value;
    },
    setUserAuthenticationErrors(value) {
      this.userAuthenticationErrors = value;
    },
    setReportToSubmit(value) {
      this.reportToSubmit = value;
    },
    async getInspections(brkey) {
      let url = INSPECTION_ENDPOINT.GET_INSPECTIONS;
      url = url.replace("{brkey}", brkey);
      await axios
        .get(url)
        .then((result) => {
          this.inspections[brkey] = result.data?.data?.sort(
            (a, b) => new Date(b.inspectionDate) - new Date(a.inspectionDate),
          );
        })
        .catch((e) => {
          let error = Object.assign(e, {
            method: "getInspections",
            brkey,
          });
          LOGGER.logException(error);
        });
    },
    saveFeatureInspectionDetails(detail) {
      let featureFound = this.featureIntersectionTableData.find((feature) => feature.featureKey == detail.featureKey);
      if (featureFound) {
        let structureRoadwaysEntry = this.selectedInspection?.structureRoadways?.find(
          (roadway) => roadway.featureKey === featureFound.featureKey,
        );
        if (structureRoadwaysEntry) {
          structureRoadwaysEntry.accCount = detail.accCount;
          structureRoadwaysEntry.accRate = detail.accRate;
          structureRoadwaysEntry.accRisk = detail.accRisk;
          structureRoadwaysEntry.adtClass = detail.adtClass;
          structureRoadwaysEntry.adtFuture = detail.adtFuture;
          structureRoadwaysEntry.adtFutYear = detail.adtFutYear;
          structureRoadwaysEntry.adtTotal = detail.adtTotal;
          structureRoadwaysEntry.adtYear = detail.adtYear;
          structureRoadwaysEntry.aRoadwayWidthFar = detail.aRoadwayWidthFar;
          structureRoadwaysEntry.aRoadWidth = detail.aRoadWidth;
          structureRoadwaysEntry.bypassLen = detail.bypassLen;
          structureRoadwaysEntry.critFeat = detail.critFeat;
          structureRoadwaysEntry.critTrav = detail.critTrav;
          structureRoadwaysEntry.crossingBridgeNumber = detail.crossingBridgeNumber;
          structureRoadwaysEntry.defHwy = detail.defHwy;
          structureRoadwaysEntry.detSpeed = detail.detSpeed;
          structureRoadwaysEntry.dirSuffix = detail.dirSuffix;
          structureRoadwaysEntry.docRefKey = detail.docRefKey;
          structureRoadwaysEntry.featureLocation = detail.featureLocation;
          structureRoadwaysEntry.fedLandHwy = detail.fedLandHwy;
          structureRoadwaysEntry.funcClass = detail.funcClass;
          structureRoadwaysEntry.hclrInv = detail.hclrInv;
          structureRoadwaysEntry.kindHwy = detail.kindHwy;
          structureRoadwaysEntry.kmPost = detail.kmPost;
          structureRoadwaysEntry.lanes = detail.lanes;
          structureRoadwaysEntry.levlSrvc = detail.levlSrvc;
          structureRoadwaysEntry.lrsInvrt = detail.lrsInvrt;
          structureRoadwaysEntry.maxUsableVerticalClearance = detail.maxUsableVerticalClearance;
          structureRoadwaysEntry.nbirWFlag = detail.nbirWFlag;
          structureRoadwaysEntry.nhsInd = detail.nhsInd;
          structureRoadwaysEntry.notes = detail.notes;
          structureRoadwaysEntry.numMedian = detail.numMedian;
          structureRoadwaysEntry.onBaseNet = detail.onBaseNet;
          structureRoadwaysEntry.onUnder = detail.onUnder;
          structureRoadwaysEntry.onUnderAdj = detail.onUnderAdj;
          structureRoadwaysEntry.rId = detail.rId;
          structureRoadwaysEntry.rinspDone = detail.rinspDone;
          structureRoadwaysEntry.roadSpeed = detail.roadSpeed;
          structureRoadwaysEntry.roadwayName = detail.roadwayName;
          structureRoadwaysEntry.roadWidth = detail.roadWidth;
          structureRoadwaysEntry.routeNum = detail.routeNum;
          structureRoadwaysEntry.rTrigger = detail.rTrigger;
          structureRoadwaysEntry.schoolBus = detail.schoolBus;
          structureRoadwaysEntry.snbiFeatureType = detail.snbiFeatureType;
          structureRoadwaysEntry.snbiUrbanCd = detail.snbiUrbanCd;
          structureRoadwaysEntry.subrtNum = detail.subrtNum;
          structureRoadwaysEntry.tenYrCnt = detail.tenYrCnt;
          structureRoadwaysEntry.tollFac = detail.tollFac;
          structureRoadwaysEntry.trafficDir = detail.trafficDir;
          structureRoadwaysEntry.transitRt = detail.transitRt;
          structureRoadwaysEntry.truckNet = detail.truckNet;
          structureRoadwaysEntry.truckPct = detail.truckPct;
          structureRoadwaysEntry.userRwKey1 = detail.userRwKey1;
          structureRoadwaysEntry.userRwKey2 = detail.userRwKey2;
          structureRoadwaysEntry.userRwKey3 = detail.userRwKey3;
          structureRoadwaysEntry.userRwKey4 = detail.userRwKey4;
          structureRoadwaysEntry.userRwKey5 = detail.userRwKey5;
          structureRoadwaysEntry.vclrInv = detail.vclrInv;
          structureRoadwaysEntry.farApproachRoadwayWidth = detail.farApproachRoadwayWidth;
          structureRoadwaysEntry.nearApproachRoadwayWidth = detail.nearApproachRoadwayWidth;
        }

        let userRWaysEntry = this.selectedInspection?.userRWays?.find(
          (roadway) => roadway.featureKey === featureFound.featureKey,
        );
        if (userRWaysEntry) {
          userRWaysEntry.aarDotNum = detail.aarDotNum;
          userRWaysEntry.addOperDesc = detail.addOperDesc;
          userRWaysEntry.adminJuris = detail.adminJuris;
          userRWaysEntry.adtt = detail.adtt;
          userRWaysEntry.adttYear = detail.adttYear;
          userRWaysEntry.atttNetwork = detail.atttNetwork;
          userRWaysEntry.bridgeMed = detail.bridgeMed;
          userRWaysEntry.calcScourDepth = detail.calcScourDepth;
          userRWaysEntry.ccvNetNetwork = detail.ccvNetNetwork;
          userRWaysEntry.defVertClearLeft = detail.defVertClearLeft;
          userRWaysEntry.defVertClearRight = detail.defVertClearRight;
          userRWaysEntry.depClass1 = detail.depClass1;
          userRWaysEntry.depClass2 = detail.depClass2;
          userRWaysEntry.depClass3 = detail.depClass3;
          userRWaysEntry.depPermitType = detail.depPermitType;
          userRWaysEntry.depTimeframe = detail.depTimeframe;
          userRWaysEntry.designFloodElev = detail.designFloodElev;
          userRWaysEntry.designFloodFreq = detail.designFloodFreq;
          userRWaysEntry.designFloodMagnitude = detail.designFloodMagnitude;
          userRWaysEntry.designFloodVel = detail.designFloodVel;
          userRWaysEntry.elecTrackQty = detail.elecTrackQty;
          userRWaysEntry.featureType = detail.featureType;
          userRWaysEntry.fedAid = detail.fedAid;
          userRWaysEntry.fishable = detail.fishable;
          userRWaysEntry.govtCont = detail.govtCont;
          userRWaysEntry.highwayInd = detail.highwayInd;
          userRWaysEntry.hwySysType = detail.hwySysType;
          userRWaysEntry.interstateNetwork = detail.interstateNetwork;
          userRWaysEntry.maxWaterSurfElev = detail.maxWaterSurfElev;
          userRWaysEntry.maxWaterSurfYear = detail.maxWaterSurfYear;
          userRWaysEntry.medWidth = detail.medWidth;
          userRWaysEntry.minOverVertClearLeft = detail.minOverVertClearLeft;
          userRWaysEntry.minOverVertClearRight = detail.minOverVertClearRight;
          userRWaysEntry.navigable = detail.navigable;
          userRWaysEntry.nhsNetwork = detail.nhsNetwork;
          userRWaysEntry.notes = detail.notes;
          userRWaysEntry.offset = detail.offset;
          userRWaysEntry.onUnder = detail.onUnder;
          userRWaysEntry.onUnderInd = detail.onUnderInd;
          userRWaysEntry.oversizeBypassLength = detail.oversizeBypassLength;
          userRWaysEntry.paCounty = detail.paCounty;
          userRWaysEntry.polutantDesc = detail.polutantDesc;
          userRWaysEntry.primaryWaterway = detail.primaryWaterway;
          userRWaysEntry.repaveDate = detail.repaveDate;
          userRWaysEntry.rmsRefKey = detail.rmsRefKey;
          userRWaysEntry.rmsRoadwayBPN = detail.rmsRoadwayBPN;
          userRWaysEntry.roadwayLabel1 = detail.roadwayLabel1;
          userRWaysEntry.roadwayLabel2 = detail.roadwayLabel2;
          userRWaysEntry.roadwayLabel3 = detail.roadwayLabel3;
          userRWaysEntry.roadwayLabel4 = detail.roadwayLabel4;
          userRWaysEntry.routeDesignation = detail.routeDesignation;
          userRWaysEntry.rrMilePost = detail.rrMilePost;
          userRWaysEntry.rrName = detail.rrName;
          userRWaysEntry.rrServStatus = detail.rrServStatus;
          userRWaysEntry.rrServiceType = detail.rrServiceType;
          userRWaysEntry.segNum = detail.segNum;
          userRWaysEntry.skew = detail.skew;
          userRWaysEntry.spanDesc = detail.spanDesc;
          userRWaysEntry.srNum = detail.srNum;
          userRWaysEntry.stateHwyNetwork = detail.stateHwyNetwork;
          userRWaysEntry.streamDrainArea = detail.streamDrainArea;
          userRWaysEntry.streamRestrictDesc = detail.streamRestrictDesc;
          userRWaysEntry.totHorClearLeft = detail.totHorClearLeft;
          userRWaysEntry.totHorClearRight = detail.totHorClearRight;
          userRWaysEntry.tttnNetwork = detail.tttnNetwork;
          userRWaysEntry.urbanRural = detail.urbanRural;
          userRWaysEntry.vertClearSign = detail.vertClearSign;
          userRWaysEntry.vrtClearSignR = detail.vrtClearSignR;
          userRWaysEntry.vrtClrPostLeftFt = detail.vrtClrPostLeftFt;
          userRWaysEntry.vrtClrPostLeftInch = detail.vrtClrPostLeftInch;
          userRWaysEntry.vrtClrPostRightFt = detail.vrtClrPostRightFt;
          userRWaysEntry.vrtClrPostRightInch = detail.vrtClrPostRightInch;
          userRWaysEntry.waterflowDir = detail.waterflowDir;
        }
      }
    },
    setShowSubmitLaterMessage(value) {
      this.showSubmitLaterMessage = value;
    },
  },
});
